// Property management → Messages (the demo's pmMessages): every conversation residents of this development have
// started, and any another team has brought you into — read through GET /messages?propertyId=, the same list as the
// header's messages icon. Open threads on the left, Closed folded away below; a thread opens on the right.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Loader2, MessageSquarePlus } from 'lucide-react';
import { EmptyState } from '../ui';
import { ThreadRow } from '../messages/ThreadRow';
import { Thread } from '../messages/Thread';
import { messagesApi, type ConversationSummary } from '../../lib/comms';

export function MessagesPanel({ propertyId }: { propertyId: string }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [showClosed, setShowClosed] = useState(false);
  const open = useQuery({ queryKey: ['/messages', { propertyId, status: 'OPEN' }], queryFn: () => messagesApi.list({ propertyId, status: 'OPEN', perPage: 100 }), refetchInterval: 30_000 });
  const closed = useQuery({ queryKey: ['/messages', { propertyId, status: 'CLOSED' }], queryFn: () => messagesApi.list({ propertyId, status: 'CLOSED', perPage: 100 }) });
  const staff = open.data?.viewer.side === 'STAFF';
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['/messages'] }); void qc.invalidateQueries({ queryKey: ['/my-day'] }); };
  const openRows = open.data?.items ?? [];
  const closedRows = closed.data?.items ?? [];

  const section = (title: string, n: number, extra?: React.ReactNode) => (
    <div className="mb-2 flex items-center gap-2">
      <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-400">{title}</h3>
      <span className="h-px flex-1 bg-ink-100" />
      {extra}
      <span className="rounded-full bg-ink-100 px-2 py-0.5 text-[11px] font-medium text-ink-600">{n}</span>
    </div>
  );
  const rows = (list: ConversationSummary[]) => (
    <div className="surface divide-y divide-ink-100 overflow-hidden">
      {list.map((c) => <ThreadRow key={c.id} c={c} staff={staff} active={c.id === selected} onOpen={() => setSelected(c.id)} />)}
    </div>
  );

  return (
    <div className="space-y-3">
      <p className="text-[12px] text-ink-500">
        Everything residents in this building have sent, and anything another team has brought you into.{' '}
        <Link to="/app/messages" className="font-medium text-bronze-600 hover:underline">All developments</Link>
      </p>
      <div className="grid gap-2.5 lg:grid-cols-[minmax(300px,380px)_1fr]">
        <div className={`min-w-0 space-y-2.5 ${selected ? 'hidden lg:block' : ''}`}>
          <section>
            {section('Open', openRows.length, open.isFetching ? <Loader2 className="h-3.5 w-3.5 animate-spin text-bronze-400" /> : null)}
            {open.isLoading ? <div className="surface space-y-3 p-4">{[0, 1, 2].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-ink-100" />)}</div>
              : open.error ? <EmptyState icon="TriangleAlert" title="Could not load messages" body={(open.error as Error).message} />
                : openRows.length ? rows(openRows)
                  : <div className="surface px-4 py-8 text-center text-[12.5px] text-ink-500">Nothing open from this development.</div>}
          </section>
          {closedRows.length > 0 && (
            <section>
              <button type="button" onClick={() => setShowClosed((v) => !v)} className="w-full text-left" aria-expanded={showClosed}>
                {section('Closed', closedRows.length, showClosed ? <ChevronDown className="h-3.5 w-3.5 text-ink-400" /> : <ChevronRight className="h-3.5 w-3.5 text-ink-400" />)}
              </button>
              {showClosed && rows(closedRows)}
            </section>
          )}
        </div>
        <div className={`min-w-0 ${selected ? '' : 'hidden lg:block'}`}>
          {selected ? <Thread id={selected} onBack={() => setSelected(null)} onChanged={refresh} /> : (
            <div className="surface grid min-h-[420px] place-items-center p-8 text-center">
              <div>
                <MessageSquarePlus className="mx-auto h-6 w-6 text-bronze-400" />
                <p className="mt-3 text-[15px] font-semibold text-ink-900">Pick a conversation</p>
                <p className="mt-1 text-[12.5px] text-ink-500">Unread threads are marked; the oldest unanswered also sit on My Day.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
